import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Card } from '@/components/admin/ui';
import {
  BarList,
  KpiTile,
} from '@/components';
import type { BreakdownEntry, DeviceInfoAnalytics } from '@/lib/admin-types';
import { track } from '@/lib/analytics';
import { apiJson } from '@/lib/api';

const HINTS = {
  devices: 'Every registered installation, including ones that have not reported hardware yet.',
  withHardware:
    'Installations that sent a hardware id or a total RAM value. Older app builds stay in the rest until they update.',
  tablets: 'Installations that reported is_tablet = true.',
  phones: 'Installations that reported is_tablet = false. Unknown tablet state is left out of this count.',
  emulators: 'Installations that reported is_emulator = true.',
  medianRam: 'Median installed RAM among devices that reported total_memory_mb.',
  ram: 'Installed RAM, folded into capacity bands. Unknown means the device has not reported memory yet.',
  disk: 'Installed disk, folded into capacity bands.',
} as const;

function formatMemory(mb: number | null | undefined): string {
  if (mb == null) return '—';
  const gb = mb / 1024;
  if (gb >= 10) return `${Math.round(gb)} GB`;
  return `${gb.toFixed(1)} GB`;
}

function BreakdownCard({
  title,
  hint,
  data,
  loading,
  preserveOrder = false,
}: {
  title: string;
  hint: string;
  data: BreakdownEntry[];
  loading: boolean;
  preserveOrder?: boolean;
}) {
  return (
    <Card padding="md" className="cursor-help" title={hint}>
      <h2 className="mb-3 text-sm font-semibold text-nova-foreground">{title}</h2>
      {loading ? (
        <div className="h-40 animate-pulse rounded-lg bg-nova-muted" />
      ) : (
        <BarList data={data} emptyLabel="No devices yet" preserveOrder={preserveOrder} />
      )}
    </Card>
  );
}

function DeviceInfoPage() {
  useEffect(() => {
    track('Device Info Analytics');
  }, []);

  const infoQ = useQuery({
    queryKey: ['admin', 'device-info'],
    queryFn: () => apiJson<DeviceInfoAnalytics>('/v1/admin/analytics/device-info'),
  });

  const summary = infoQ.data?.summary;
  const loading = infoQ.isLoading;
  const data = infoQ.data;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Device info</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Installed hardware reported by the app. Counts are all-time.
        </p>
      </div>

      {infoQ.error && (
        <p className="text-sm text-red-600">
          {(infoQ.error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void infoQ.refetch()}>
            Retry
          </button>
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <KpiTile label="Devices" value={summary?.total_devices ?? 0} loading={loading} tooltip={HINTS.devices} />
        <KpiTile
          label="With hardware"
          value={summary?.with_hardware ?? 0}
          loading={loading}
          tooltip={HINTS.withHardware}
        />
        <KpiTile
          label="Median RAM"
          value={formatMemory(summary?.median_memory_mb)}
          loading={loading}
          tooltip={HINTS.medianRam}
        />
        <KpiTile label="Phones" value={summary?.phones ?? 0} loading={loading} tooltip={HINTS.phones} />
        <KpiTile label="Tablets" value={summary?.tablets ?? 0} loading={loading} tooltip={HINTS.tablets} />
        <KpiTile
          label="Emulators"
          value={summary?.emulators ?? 0}
          loading={loading}
          tooltip={HINTS.emulators}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <BreakdownCard
          title="RAM"
          hint={HINTS.ram}
          data={data?.by_ram_bucket ?? []}
          loading={loading}
          preserveOrder
        />
        <BreakdownCard
          title="Disk"
          hint={HINTS.disk}
          data={data?.by_disk_bucket ?? []}
          loading={loading}
          preserveOrder
        />
        <BreakdownCard
          title="Hardware id"
          hint="Model identifier from the device, such as iPhone14,5. Top 10, then Other."
          data={data?.by_hardware_id ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="Device model"
          hint="Marketing device model reported by the app."
          data={data?.by_device_model ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="Brand"
          hint="Device brand, such as Apple or samsung."
          data={data?.by_brand ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="CPU architecture"
          hint="Supported ABIs reported by the device, for example arm64-v8a."
          data={data?.by_cpu_abis ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="Platform"
          hint="ios, android, or web, as stored on the device row."
          data={data?.by_platform ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="OS version"
          hint="Client-reported operating system version."
          data={data?.by_os_version ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="System name"
          hint="Operating system name, such as iOS or Android."
          data={data?.by_system_name ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="Screen"
          hint="Screen size and scale, formatted as width x height @ scale."
          data={data?.by_screen ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="Android API level"
          hint="Android API level. iOS devices leave this unset."
          data={data?.by_api_level ?? []}
          loading={loading}
        />
        <BreakdownCard
          title="Tablet"
          hint="Whether the install reported itself as a tablet."
          data={data?.by_tablet ?? []}
          loading={loading}
          preserveOrder
        />
        <BreakdownCard
          title="Emulator"
          hint="Whether the install reported itself as an emulator or simulator."
          data={data?.by_emulator ?? []}
          loading={loading}
          preserveOrder
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/device-info')({
  component: DeviceInfoPage,
});
